import { MOCK_METADATA } from './mockData';

const TENANT_PROFILE_TYPES = ['INQUILINO', 'INQUILINO_ESTUDANTE', 'CONJUGE_INQUILINO'];
const GUARANTOR_PROFILE_TYPES = ['FIADOR', 'FIADOR_ESTUDANTE', 'CONJUGE_FIADOR'];

const canGroupMatchTipoCondition = (groupTypes, operator, selectedValues) => {
  const values = Array.isArray(selectedValues) ? selectedValues : [selectedValues];

  if (!values.length) {
    return true;
  }

  if (operator === 'in') {
    return groupTypes.some((type) => values.includes(type));
  }

  if (operator === 'not_in') {
    return groupTypes.some((type) => !values.includes(type));
  }

  return true;
};

const canGroupMatchAllTipoConditions = (groupTypes, tipoConditions) =>
  tipoConditions.every((condition) =>
    canGroupMatchTipoCondition(groupTypes, condition.operator, condition.value)
  );

export const getRequiredParecerProfiles = (conditions) => {
  const tipoConditions = (conditions || []).filter((condition) => condition.field === 'tipo_pretendente');

  if (tipoConditions.length === 0) {
    return { requiresTenant: false, requiresGuarantor: false, requiresAny: true };
  }

  return {
    requiresTenant: canGroupMatchAllTipoConditions(TENANT_PROFILE_TYPES, tipoConditions),
    requiresGuarantor: canGroupMatchAllTipoConditions(GUARANTOR_PROFILE_TYPES, tipoConditions),
    requiresAny: false,
  };
};

export const getParecerValidationErrors = (formData) => {
  const { requiresTenant, requiresGuarantor, requiresAny } = getRequiredParecerProfiles(formData.conditions);
  const hasTenantText = formData.tenant_recommendation_text.trim().length > 0;
  const hasGuarantorText = formData.guarantor_recommendation_text.trim().length > 0;

  if (requiresAny) {
    const missingAny = !hasTenantText && !hasGuarantorText;
    return {
      requiresAny: true,
      requiresTenant: false,
      requiresGuarantor: false,
      tenant: missingAny,
      guarantor: missingAny,
    };
  }

  return {
    requiresAny: false,
    requiresTenant,
    requiresGuarantor,
    tenant: requiresTenant && !hasTenantText,
    guarantor: requiresGuarantor && !hasGuarantorText,
  };
};

export const arePareceresValid = (formData) => {
  const errors = getParecerValidationErrors(formData);
  return !errors.tenant && !errors.guarantor;
};

export const getParecerSectionHint = (conditions) => {
  const { requiresTenant, requiresGuarantor, requiresAny } = getRequiredParecerProfiles(conditions);

  if (requiresAny) {
    return 'Preencha ao menos um dos campos abaixo (Inquilinos ou Fiadores).';
  }

  if (requiresTenant && requiresGuarantor) {
    return 'Preencha o parecer para Inquilinos e Fiadores, conforme os perfis selecionados nos critérios.';
  }

  if (requiresTenant) {
    return 'Preencha o parecer para Inquilinos, conforme o perfil selecionado nos critérios.';
  }

  if (requiresGuarantor) {
    return 'Preencha o parecer para Fiadores, conforme o perfil selecionado nos critérios.';
  }

  return 'Nenhum parecer é obrigatório para os perfis selecionados nos critérios.';
};

export const getParecerSectionErrorMessage = (formData) => {
  const { tenant, guarantor, requiresAny } = getParecerValidationErrors(formData);

  if (requiresAny && tenant) {
    return 'Informe o texto para Inquilinos ou Fiadores para continuar.';
  }

  const missing = [];

  if (tenant) missing.push('Inquilinos');
  if (guarantor) missing.push('Fiadores');

  if (missing.length === 0) {
    return '';
  }

  if (missing.length === 2) {
    return 'Informe o texto para Inquilinos e Fiadores para continuar.';
  }

  return `Informe o texto para ${missing[0]} para continuar.`;
};

const isNegativacaoValorEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

const isNegativacaoPercentualEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

const isOrigemRendaSomaRendaEnabled = (subCondition) =>
  subCondition && subCondition.enabled === true;

const isOrigemRendaSomaRendaValueValid = (subCondition) => {
  if (!subCondition || !subCondition.operator) return false;

  if (subCondition.operator === 'between') {
    if (!subCondition.value || typeof subCondition.value !== 'object') return false;
    if (!subCondition.value.min && subCondition.value.min !== 0) return false;
    if (!subCondition.value.max && subCondition.value.max !== 0) return false;
    if (Number(subCondition.value.min) > Number(subCondition.value.max)) return false;
    return true;
  }

  return subCondition.value !== '' && subCondition.value !== null && subCondition.value !== undefined;
};

const isConditionValueValid = (condition, selectedField) => {
  if (condition.value === '' || condition.value === null || condition.value === undefined) {
    return false;
  }

  if (selectedField.type === 'multi_select' && Array.isArray(condition.value) && condition.value.length === 0) {
    return false;
  }

  if (condition.field === 'origem_renda' && condition.operator === 'in') {
    const somaRenda = condition.sub_conditions && condition.sub_conditions.soma_renda;
    if (isOrigemRendaSomaRendaEnabled(somaRenda) && !isOrigemRendaSomaRendaValueValid(somaRenda)) {
      return false;
    }
  }

  if (condition.operator === 'between') {
    if (!condition.value.min && condition.value.min !== 0) return false;
    if (!condition.value.max && condition.value.max !== 0) return false;
    if (condition.field === 'faixa_valor_locacao' && Number(condition.value.min) > Number(condition.value.max)) {
      return false;
    }
  }

  if (selectedField.type === 'compound_boolean') {
    const compoundValues = Array.isArray(condition.value)
      ? condition.value
      : typeof condition.value === 'boolean'
        ? [condition.value]
        : [];

    if (compoundValues.length === 0) return false;

    if (compoundValues.includes(true)) {
      if (!selectedField.sub_fields) return false;

      if (condition.field === 'negativacao') {
        const subConditions = condition.sub_conditions || {};
        const quantidade = subConditions.quantidade;

        if (!quantidade || !quantidade.operator) return false;
        if (quantidade.value === '' || quantidade.value === null || quantidade.value === undefined) {
          return false;
        }

        const valorEnabled = isNegativacaoValorEnabled(subConditions.valor);
        const percentualEnabled = isNegativacaoPercentualEnabled(subConditions.valor_percentual);

        if (!valorEnabled && !percentualEnabled) return false;

        if (valorEnabled) {
          const valor = subConditions.valor;
          if (!valor.operator) return false;
          if (valor.value === '' || valor.value === null || valor.value === undefined) return false;
        }

        if (percentualEnabled) {
          const percentual = subConditions.valor_percentual;
          if (!percentual.operator) return false;
          if (percentual.value === '' || percentual.value === null || percentual.value === undefined) {
            return false;
          }
          if (Number(percentual.value) > 100) return false;
        }

        return true;
      }

      return selectedField.sub_fields.every((subField) => {
        const subCondition = condition.sub_conditions && condition.sub_conditions[subField.id];

        if (!subCondition || !subCondition.operator) return false;
        if (subCondition.value === '' || subCondition.value === null || subCondition.value === undefined) {
          return false;
        }

        return true;
      });
    }

    return true;
  }

  return true;
};

export const isConditionValid = (condition) => {
  if (!condition.field || !condition.operator) return false;

  const selectedField = MOCK_METADATA.fields.find(f => f.id === condition.field);
  if (!selectedField) return false;

  return isConditionValueValid(condition, selectedField);
};

export const getConditionErrors = (condition) => {
  const errors = {
    field: false,
    operator: false,
    value: false,
    subFields: {},
    missingValorCriterion: false
  };

  if (!condition.field) {
    errors.field = true;
    return errors;
  }

  const selectedField = MOCK_METADATA.fields.find(f => f.id === condition.field);
  if (!selectedField) {
    errors.field = true;
    return errors;
  }

  if (!condition.operator) {
    errors.operator = true;
    return errors;
  }

  if (!isConditionValueValid(condition, selectedField)) {
    errors.value = true;
  }

  if (condition.field === 'origem_renda' && condition.operator === 'in') {
    const somaRenda = condition.sub_conditions && condition.sub_conditions.soma_renda;
    if (isOrigemRendaSomaRendaEnabled(somaRenda) && !isOrigemRendaSomaRendaValueValid(somaRenda)) {
      errors.subFields.soma_renda = true;
    }
  }

  if (selectedField.type === 'compound_boolean' && condition.field === 'negativacao') {
    const compoundValues = Array.isArray(condition.value)
      ? condition.value
      : typeof condition.value === 'boolean'
        ? [condition.value]
        : [];

    if (compoundValues.includes(true)) {
      const subConditions = condition.sub_conditions || {};
      const quantidade = subConditions.quantidade;

      if (!quantidade || !quantidade.operator ||
        quantidade.value === '' || quantidade.value === null || quantidade.value === undefined) {
        errors.subFields.quantidade = true;
      }

      const valorEnabled = isNegativacaoValorEnabled(subConditions.valor);
      const percentualEnabled = isNegativacaoPercentualEnabled(subConditions.valor_percentual);

      if (!valorEnabled && !percentualEnabled) {
        errors.missingValorCriterion = true;
      }

      if (valorEnabled) {
        const valor = subConditions.valor;
        if (!valor || !valor.operator ||
          valor.value === '' || valor.value === null || valor.value === undefined) {
          errors.subFields.valor = true;
        }
      }

      if (percentualEnabled) {
        const percentual = subConditions.valor_percentual;
        if (!percentual || !percentual.operator ||
          percentual.value === '' || percentual.value === null || percentual.value === undefined ||
          Number(percentual.value) > 100) {
          errors.subFields.valor_percentual = true;
        }
      }
    }
  } else if (selectedField.type === 'compound_boolean' && selectedField.sub_fields) {
    const compoundValues = Array.isArray(condition.value)
      ? condition.value
      : typeof condition.value === 'boolean'
        ? [condition.value]
        : [];

    if (compoundValues.includes(true)) {
      selectedField.sub_fields.forEach((subField) => {
        const subCondition = condition.sub_conditions && condition.sub_conditions[subField.id];
        if (!subCondition || !subCondition.operator ||
          subCondition.value === '' || subCondition.value === null || subCondition.value === undefined) {
          errors.subFields[subField.id] = true;
        }
      });
    }
  }

  return errors;
};

export const isFormDataValid = (formData) => {
  if (!formData.name.trim() ||
    !arePareceresValid(formData) ||
    formData.conditions.length === 0) {
    return false;
  }

  return formData.conditions.every(isConditionValid);
};

/** @deprecated Use arePareceresValid instead */
export const hasRecommendationText = (formData) => arePareceresValid(formData);
